import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { MapPin, Phone, Mail, Clock, MessageCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const Contact = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  
  const contactInfo = [
    {
      icon: MapPin,
      title: t('contact.info.address'),
      details: [
        t('contact.info.addressValue')
      ],
    },
    {
      icon: Phone,
      title: t('contact.info.phone'),
      details: [
        <a key="phone1" href="tel:+919036493100" className="hover:text-[#4A5F8F] hover:underline transition-colors">+91 9036493100</a>,
        <a key="phone2" href="tel:+918539466804" className="hover:text-[#4A5F8F] hover:underline transition-colors">+91 8539466804</a>
      ],
    },
    {
      icon: Mail,
      title: t('contact.info.email'),
      details: [
        <a key="email" href="mailto:sales@dharanipipes.com" className="hover:text-[#4A5F8F] hover:underline transition-colors break-all">sales@dharanipipes.com</a>
      ],
    },
    {
      icon: Clock,
      title: t('contact.info.hours'),
      details: [t('contact.info.hoursValue'), t('contact.info.sundayClosed')],
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <section className="bg-gradient-to-br from-[#4A5F8F] to-[#2C3E5F] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <MessageCircle className="w-16 h-16 mx-auto mb-6" />
            <h1 className="text-5xl font-bold mb-6">{t('contact.title')}</h1>
            <p className="text-xl text-gray-100 max-w-3xl mx-auto">
              {t('contact.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {contactInfo.map((info, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden flex flex-col h-full">
                <CardContent className="p-8 text-center flex-grow">
                  <div className="w-16 h-16 bg-[#4A5F8F] bg-opacity-10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <info.icon className="w-8 h-8 text-[#4A5F8F]" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">{info.title}</h3>
                  <div className="space-y-2">
                    {info.details.map((detail, idx) => (
                      <div key={idx} className="text-gray-600">
                        {detail}
                      </div>
                    ))}
                  </div>
                </CardContent>
                {/* Blue accent line at bottom */}
                <div className="h-1 bg-[#4A5F8F] mt-auto" />
              </div>
            ))}
          </div>

          {/* Map and Company Info */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Map Placeholder */}
            <Card className="border-none shadow-lg overflow-hidden h-[400px] lg:h-full">
              <CardContent className="p-0 h-full">
                <div className="h-full bg-gray-200 relative">
                  <iframe
                    title="Dharani Pipes Location"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3847.4204559903446!2d76.12293729999999!3d15.3536989!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bb8352f9edcda87%3A0x3c4cbe684b3a21b1!2sDharani%20Pipes%20Pvt.Ltd!5e0!3m2!1sen!2sin!4v1773491363749!5m2!1sen!2sin"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  ></iframe>
                </div>
              </CardContent>
            </Card>

            {/* Company Details */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">{currentLang === 'en' ? 'Dharani Pipes Private Limited' : 'ಧರಣಿ ಪೈಪ್ಸ್ ಪ್ರೈವೇಟ್ ಲಿಮಿಟೆಡ್'}</h2>
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('contact.aboutLocation.title')}</h3>
                  <p className="text-gray-700 leading-relaxed">
                    {t('contact.aboutLocation.description')}
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('contact.visitUs.title')}</h3>
                  <p className="text-gray-700 leading-relaxed">
                    {t('contact.visitUs.description')}
                  </p>
                </div>

                <Card className="border-2 border-[#4A5F8F] border-opacity-20 bg-blue-50">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">{t('contact.getInTouch.title')}</h3>
                    <p className="text-gray-700 mb-4">
                      {t('contact.getInTouch.description')}
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-center text-[#4A5F8F]">
                        <Phone className="w-5 h-5 mr-3" />
                        <div>
                          <a href="tel:+919036493100" className="font-medium hover:underline block">
                            +91 9036493100
                          </a>
                          <a href="tel:+918539466804" className="font-medium hover:underline block">
                            +91 8539466804
                          </a>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <div className="bg-gradient-to-br from-[#4A5F8F] to-[#2C3E5F] text-white p-6 rounded-lg">
                  <h3 className="text-xl font-semibold mb-2">{t('home.features.iso.title')}</h3>
                  <p className="text-gray-100">
                    {t('home.features.iso.description')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
